import React from "react";
import { Link } from "react-router-dom";

import SEO from "../../seo/SEO";

import {
  Smartphone,
  ArrowRight,
  CheckCircle2,
  Wifi,
  ShieldCheck,
  LayoutDashboard,
  Cloud,
  BellRing,
} from "lucide-react";

import "./MobileAppDevelopment.css";


/* =========================================================
   MOBILE APP SERVICES
========================================================= */

const mobileServices = [
  {
    icon: <Smartphone size={28} />,

    title: "Android App Development",

    description:
      "Custom Android applications with scalable backend systems, realtime APIs and modern mobile experiences.",
  },

  {
    icon: <Wifi size={28} />,

    title: "Realtime Mobile Systems",

    description:
      "Realtime synchronization systems with notifications, GPS tracking, live updates and cloud integrations.",
  },

  {
    icon: <LayoutDashboard size={28} />,

    title: "Admin Dashboard Integration",

    description:
      "Mobile applications connected with enterprise admin dashboards, analytics and reporting systems.",
  },

  {
    icon: <Cloud size={28} />,

    title: "Cloud-Based Architecture",

    description:
      "Cloud-ready mobile app infrastructure using scalable backend systems and secure API architecture.",
  },

  {
    icon: <BellRing size={28} />,

    title: "Push Notifications",

    description:
      "Realtime notifications, order updates, alerts and customer engagement systems for modern apps.",
  },

  {
    icon: <ShieldCheck size={28} />,

    title: "Secure Authentication",

    description:
      "Secure login systems, OTP verification, JWT authentication and protected mobile workflows.",
  },
];


/* =========================================================
   TECHNOLOGY STACK
========================================================= */

const technologies = [
  "React Native",
  "Flutter",
  "FastAPI",
  "Node.js",
  "PostgreSQL",
  "MongoDB",
  "Firebase",
  "Docker",
  "AWS",
  "Redis",
];


/* =========================================================
   MOBILE APP DEVELOPMENT PAGE
========================================================= */

export default function MobileAppDevelopment() {

  /* =======================================================
     CANONICAL URL
  ======================================================= */

  const canonicalUrl =
    "https://www.bairagi-consultants.com/mobile-app-development";


  /* =======================================================
     SERVICE STRUCTURED DATA
  ======================================================= */

  const serviceSchema = {
    "@context": "https://schema.org",

    "@type": "Service",

    name: "Mobile App Development Services",

    serviceType:
      "Custom Mobile Application Development",

    url: canonicalUrl,

    description:
      "Custom Android, iOS and cross-platform mobile application development with scalable backend systems, APIs, cloud infrastructure, realtime features and enterprise integrations.",

    provider: {
      "@type": "Organization",

      name: "Bairagi Consultants",

      url: "https://www.bairagi-consultants.com",
    },

    areaServed: {
      "@type": "Country",

      name: "India",
    },
  };


  return (
    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="Mobile App Development Company in India | Bairagi Consultants"
        description="Mobile app development company in India building Android, iOS and cross-platform applications with React Native, scalable APIs, cloud infrastructure and enterprise integrations."
        path="/mobile-app-development"
        schema={serviceSchema}
      />


      <div className="mobile-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="mobile-hero mobile-container">

          {/* LEFT */}

          <div className="mobile-hero-content">

            <span className="mobile-badge">
              Mobile App Development
            </span>


            <h1 className="mobile-title">

              Scalable

              <span>
                {" "}Mobile Apps
              </span>

              <br />

              For Modern Businesses

            </h1>


            <p className="mobile-description">

              We build powerful Android, iOS and
              cross-platform mobile applications
              with scalable backend systems,
              realtime infrastructure and modern
              cloud-ready architecture.

            </p>


            {/* ACTIONS */}

            <div className="mobile-actions">

              <Link
                to="/contact?service=mobile-app"
                className="mobile-primary-btn"
              >

                Start Your App Project

                <ArrowRight size={18} />

              </Link>


              <Link
                to="/case-studies"
                className="mobile-secondary-btn"
              >

                View Mobile Solutions

              </Link>

            </div>

          </div>


          {/* RIGHT */}

          <div className="mobile-hero-card">

            <img
              src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=1400&auto=format&fit=crop"
              alt="Custom Android and mobile application development"
            />

          </div>

        </section>


        {/* =================================================
            SERVICES
        ================================================= */}

        <section className="mobile-section mobile-container">

          <div className="mobile-section-header">

            <h2>
              Mobile App
              <br />
              Development Services
            </h2>


            <p>

              Enterprise-grade mobile applications
              optimized for performance, scalability,
              realtime systems and modern customer
              experiences.

            </p>

          </div>


          <div className="mobile-services-grid">

            {mobileServices.map((service, index) => (

              <div
                key={index}
                className="mobile-service-card"
              >

                {service.icon}

                <h3>
                  {service.title}
                </h3>

                <p>
                  {service.description}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            TECH STACK
        ================================================= */}

        <section className="mobile-section mobile-container">

          <div className="mobile-section-header">

            <h2>
              Mobile App Development
              <br />
              Technology Stack
            </h2>


            <p>

              Modern mobile technologies and scalable
              backend systems for enterprise-grade
              application development.

            </p>

          </div>


          <div className="mobile-tech-stack">

            {technologies.map((tech) => (

              <span
                key={tech}
                className="mobile-tech-tag"
              >

                {tech}

              </span>

            ))}

          </div>

        </section>


        {/* =================================================
            CASE STUDY
        ================================================= */}

        <section className="mobile-section mobile-container">

          <div className="mobile-case-study">

            <span>
              Featured Mobile Solution
            </span>


            <h2>
              Driver4Me Platform
            </h2>


            <p>

              We designed and developed a scalable
              driver service platform with realtime
              GPS tracking, mobile trip management,
              analytics dashboards, emergency systems
              and cloud-based operational workflows.

            </p>


            <Link
              to="/case-studies"
              className="mobile-secondary-btn"
            >

              Explore Case Studies

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>


        {/* =================================================
            WHY CHOOSE US
        ================================================= */}

        <section className="mobile-section mobile-container">

          <div className="mobile-section-header">

            <h2>
              Why Choose Bairagi Consultants
              <br />
              for Mobile App Development?
            </h2>

          </div>


          <div className="mobile-services-grid">

            <div className="mobile-service-card">

              <CheckCircle2 size={28} />


              <h3>
                Scalable Architecture
              </h3>


              <p>

                Modern backend systems designed
                for long-term scalability and
                enterprise-grade performance.

              </p>

            </div>


            <div className="mobile-service-card">

              <CheckCircle2 size={28} />


              <h3>
                Modern Mobile UI/UX
              </h3>


              <p>

                High-quality mobile experiences
                optimized for usability, speed,
                engagement and customer retention.

              </p>

            </div>


            <div className="mobile-service-card">

              <CheckCircle2 size={28} />


              <h3>
                Realtime Cloud Systems
              </h3>


              <p>

                Cloud-ready mobile infrastructure
                with realtime synchronization,
                notifications and intelligent
                business workflows.

              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            BUSINESS APPLICATIONS
        ================================================= */}

        <section className="mobile-section mobile-container">

          <div className="mobile-section-header">

            <h2>
              Mobile Applications
              <br />
              for Business Operations
            </h2>


            <p>

              Mobile applications can be integrated
              with ecommerce platforms, CRM systems,
              ERP applications, MIS dashboards and
              other business software to provide
              employees and customers secure access
              from anywhere.

            </p>

          </div>


          <div className="mobile-services-grid">

            <div className="mobile-service-card">

              <Smartphone size={28} />

              <h3>
                Customer Applications
              </h3>

              <p>
                Mobile applications for customers,
                ecommerce buyers, service users and
                business communities.
              </p>

            </div>


            <div className="mobile-service-card">

              <LayoutDashboard size={28} />

              <h3>
                Business & Field Applications
              </h3>

              <p>
                Applications for sales teams,
                field employees, service teams and
                operational users connected with
                central business systems.
              </p>

            </div>


            <div className="mobile-service-card">

              <Cloud size={28} />

              <h3>
                Backend API Integration
              </h3>

              <p>
                Connect mobile applications with
                secure APIs, databases, cloud
                platforms and existing enterprise
                applications.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FINAL CTA
        ================================================= */}

        <section className="mobile-section mobile-container">

          <div className="mobile-cta">

            <h2>

              Ready To Build Your
              <br />

              Mobile Application?

            </h2>


            <p>

              Let's discuss your mobile application
              requirements, architecture and scalable
              backend infrastructure to build a
              modern mobile platform for your business.

            </p>


            <Link
              to="/contact?service=mobile-app"
              className="mobile-primary-btn"
            >

              Schedule Consultation

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>

      </div>

    </>
  );
}